import React, { useEffect, useMemo, useState } from "react";
import {
    Activity,
    AlertTriangle,
    CalendarDays,
    Camera,
    CheckCircle2,
    ChevronDown,
    ChevronUp,
    Clock3,
    Download,
    FileText,
    Flame,
    MapPin,
    Search,
    Store,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { motion, AnimatePresence } from "framer-motion";

import api from "../../api/axios";
import AdminNavbar from "../../components/layout/AdminNavbar";
import ReverseGeocode from "../../components/ReverseGeocode";

const vibeConfigs = {
    crushing_it: { label: "Crushing it", emoji: "ðŸš€", color: "text-emerald-500 bg-emerald-500/10 border-emerald-500/20 dark:text-emerald-400" },
    solid_day: { label: "Solid day", emoji: "ðŸ˜Ž", color: "text-[var(--accent-primary)] bg-[var(--accent-primary)]/10 border-[var(--accent-primary)]/20 dark:text-[var(--accent-primary)]" },
    getting_there: { label: "Getting there", emoji: "ðŸ“ˆ", color: "text-amber-500 bg-amber-500/10 border-amber-500/20 dark:text-amber-400" },
    rough_day: { label: "Rough day", emoji: "ðŸ”‹", color: "text-rose-500 bg-rose-500/10 border-rose-500/20 dark:text-rose-400" },
    on_fire: { label: "On fire", emoji: "ðŸ”¥", color: "text-violet-500 bg-[var(--accent-subtle)] border-[var(--accent-primary)]/20 dark:text-[var(--accent-primary)]" },
};

const statusStyles = {
    submitted: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20 dark:text-emerald-400",
    draft: "bg-amber-500/10 text-amber-600 border-amber-500/20 dark:text-amber-400",
};

const formatDate = (dateStr) =>
    new Date(dateStr).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });

const isSundayDate = (dateStr = "") => {
    const [year, month, day] = String(dateStr).split("-").map(Number);
    if (!year || !month || !day) return false;
    return new Date(year, month - 1, day).getDay() === 0;
};

const parseDateKey = (dateStr = "") => {
    const [year, month, day] = String(dateStr).split("-").map(Number);
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day);
};

const toDateKey = (date) =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

const dateKeyFromValue = (value = "") => {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "" : toDateKey(date);
};

const addOffDaysInRange = (dateSet, holidays = []) => {
    const dates = Array.from(dateSet).map(parseDateKey).filter(Boolean);
    if (!dates.length) return;
    const start = new Date(Math.min(...dates.map((date) => date.getTime())));
    const end = new Date(Math.max(...dates.map((date) => date.getTime())));
    const cursor = new Date(start);
    while (cursor <= end) {
        if (cursor.getDay() === 0) dateSet.add(toDateKey(cursor));
        cursor.setDate(cursor.getDate() + 1);
    }
    holidays.forEach((holiday) => {
        const key = dateKeyFromValue(holiday.date);
        const date = parseDateKey(key);
        if (date && date >= start && date <= end) dateSet.add(key);
    });
};

const buildBackendUrl = (path = "") => {
    if (!path || String(path).startsWith("data:") || String(path).startsWith("blob:")) return path;
    if (/^https?:\/\//i.test(path)) return path;

    const baseURL = String(api.defaults.baseURL || "").replace(/\/+$/, "");
    const normalizedPath = String(path).startsWith("/") ? path : `/${path}`;
    return `${baseURL}${normalizedPath}`;
};

const formatTeamName = (team) => {
    if (!team) return "Others";
    const raw = String(team).trim();
    const normalized = raw.toLowerCase();
    if (normalized === "software") return "Software";
    if (normalized === "embedded") return "Embedded";
    if (["designteam", "design team", "design"].includes(normalized)) return "Design Team";
    if (["sales team", "sales"].includes(normalized)) return "Sales Team";
    if (["tele marketing", "telemarketing"].includes(normalized)) return "Tele Marketing";
    if (["digital marketing", "digitalmarketing"].includes(normalized)) return "Digital Marketing";
    return raw.charAt(0).toUpperCase() + raw.slice(1);
};

const formatRoleLabel = (role = "") => {
    const value = String(role || "").trim();
    if (!value) return "";
    return value
        .split(/\s+/)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ");
};

const normalizeTeamName = (team) =>
    formatTeamName(team)
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "");

const slugifyFilePart = (value) =>
    String(value || "reports")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "") || "reports";

const inDateRange = (report, startDate, endDate) => {
    const reportDate = new Date(report.date);
    return (
        (!startDate || reportDate >= new Date(startDate)) &&
        (!endDate || reportDate <= new Date(endDate))
    );
};

const hasSalesProof = (report) => (report?.salesVisitReport?.photos || []).length > 0;
const isSalesReport = (report) => String(report?.employee?.team || "").toLowerCase().includes("sales");
const getEmployeeBlockerText = (value = "") => String(value || "").trim();
const hasEmployeeBlockerText = (value = "") => Boolean(getEmployeeBlockerText(value));

const formatPhotoLocation = (location) => {
    if (!location?.latitude || !location?.longitude) return "";
    return `${Number(location.latitude).toFixed(5)}, ${Number(location.longitude).toFixed(5)}`;
};

const toNumber = (value) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : 0;
};

const calculateDistanceMeters = (from, to) => {
    if (!from?.latitude || !from?.longitude || !to?.latitude || !to?.longitude) return 0;
    const earthRadius = 6371000;
    const lat1 = toNumber(from.latitude) * Math.PI / 180;
    const lat2 = toNumber(to.latitude) * Math.PI / 180;
    const deltaLat = (toNumber(to.latitude) - toNumber(from.latitude)) * Math.PI / 180;
    const deltaLng = (toNumber(to.longitude) - toNumber(from.longitude)) * Math.PI / 180;
    const a = Math.sin(deltaLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(deltaLng / 2) ** 2;
    return earthRadius * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const calculateRouteDistance = (points = []) =>
    points.reduce((total, point, index) => total + (index ? calculateDistanceMeters(points[index - 1], point) : 0), 0);

const formatDistance = (meters = 0) => {
    const value = Math.max(0, Number(meters || 0));
    if (value >= 1000) return `${(value / 1000).toFixed(value >= 10000 ? 1 : 2)} km`;
    return `${Math.round(value)} m`;
};

const joinManufacturingRows = (rows = [], formatter) =>
    rows
        .map(formatter)
        .filter(Boolean)
        .join(" | ");

const locationCacheKey = (location) => {
    if (!location?.latitude || !location?.longitude) return "";
    return `${Number(location.latitude).toFixed(5)},${Number(location.longitude).toFixed(5)}`;
};

const mapUrlForLocation = (location) => {
    if (!location?.latitude || !location?.longitude) return "";
    return `https://www.google.com/maps?q=${location.latitude},${location.longitude}`;
};

const embedMapUrlForLocation = (location) => {
    if (!location?.latitude || !location?.longitude) return "";
    return `https://maps.google.com/maps?q=${location.latitude},${location.longitude}&z=16&output=embed`;
};

const EODReports = () => {
    const navigate = useNavigate();

    const [employees, setEmployees] = useState([]);
    const [reports, setReports] = useState([]);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [activeReportId, setActiveReportId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [startDate, setStartDate] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - 30);
        return d.toISOString().split("T")[0];
    });
    const [endDate, setEndDate] = useState("");
    const [search, setSearch] = useState("");
    const [exportScope] = useState("selected-team");
    const [exportFormat, setExportFormat] = useState("xlsx");
    const [teams, setTeams] = useState([]);
    const [expandedTeams, setExpandedTeams] = useState({});
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [statusFilter, setStatusFilter] = useState("all");
    const [attendance, setAttendance] = useState([]);
    const [holidays, setHolidays] = useState([]);
    const [collapsedMonths, setCollapsedMonths] = useState({});
    const [locationNames, setLocationNames] = useState({});

    const toggleMonth = (monthLabel) => {
        setCollapsedMonths((prev) => ({
            ...prev,
            [monthLabel]: !prev[monthLabel],
        }));
    };

    useEffect(() => {
        document.title = "EOD Reports | Savrion Technologies";
        const fetchData = async () => {
            try {
                const params = {};
                if (startDate) params.startDate = startDate;
                if (endDate) params.endDate = endDate;
                
                const [employeeRes, reportRes, teamRes, holidayRes] = await Promise.all([
                    api.get("/api/admin/employees/all"),
                    api.get("/api/admin/eod", { params }),
                    api.get("/api/teams").catch(() => ({ data: [] })),
                    api.get("/api/admin/holidays").catch(() => ({ data: [] })),
                ]);

                const employeeData = employeeRes.data || [];
                setEmployees(employeeData);
                setReports(reportRes.data || []);
                setHolidays(holidayRes.data || []);

                const dbTeams = (teamRes.data || []).map((team) => ({
                    _id: team._id,
                    name: formatTeamName(team.name),
                    value: team.name,
                }));

                const assignedTeams = employeeData
                    .map((employee) => employee.team)
                    .filter(Boolean)
                    .map((team) => ({
                        _id: `assigned-${normalizeTeamName(team)}`,
                        name: formatTeamName(team),
                        value: team,
                    }));

                const merged = new Map();
                [...dbTeams, ...assignedTeams].forEach((team) => {
                    const key = normalizeTeamName(team.value || team.name);
                    if (key && !merged.has(key)) merged.set(key, team);
                });

                setTeams(Array.from(merged.values()).sort((a, b) => a.name.localeCompare(b.name)));
            } catch (err) {
                console.error("Failed to load EOD data:", err);
                if (err?.response && [401, 403].includes(err.response.status)) {
                    navigate("/admin/login");
                }
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [navigate]);

    useEffect(() => {
        document.title = "EOD Reports | Savrion Technologies";
        let active = true;
        const refreshReports = async () => {
            try {
                const params = {};
                if (startDate) params.startDate = startDate;
                if (endDate) params.endDate = endDate;
                const res = await api.get("/api/admin/eod", { params });
                if (active) setReports(res.data || []);
            } catch (err) {
                console.error("Failed to refresh EOD reports:", err);
            }
        };
        refreshReports();
        const intervalId = window.setInterval(refreshReports, 60000);
        return () => {
            active = false;
            window.clearInterval(intervalId);
        };
    }, [startDate, endDate]);

    useEffect(() => {
        document.title = "EOD Reports | Savrion Technologies";
        if (!selectedEmployee) return;
        const teamName = formatTeamName(selectedEmployee.team);
        setExpandedTeams((prev) => ({ ...prev, [teamName]: true }));
        setActiveReportId(null);
        setStatusFilter("all");
        setCollapsedMonths({});
    }, [selectedEmployee]);

    useEffect(() => {
        document.title = "EOD Reports | Savrion Technologies";
        if (!selectedEmployee) {
            setAttendance([]);
            return;
        }
        const fetchAttendance = async () => {
            try {
                const params = { employee: selectedEmployee._id };
                if (startDate) params.startDate = startDate;
                if (endDate) params.endDate = endDate;
                const res = await api.get("/api/admin/attendance", { params });
                setAttendance(res.data || []);
            } catch (err) {
                console.error("Failed to load attendance:", err);
            }
        };
        fetchAttendance();
    }, [selectedEmployee, startDate, endDate]);

    const filteredEmployees = useMemo(() => {
        const needle = search.toLowerCase();
        return employees
            .filter((employee) =>
                [employee.name, employee.email, employee.role, employee.team]
                    .filter(Boolean)
                    .some((value) => String(value).toLowerCase().includes(needle)),
            )
            .sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));
    }, [employees, search]);

    const groupedByTeam = useMemo(() => {
        const groups = new Map();
        teams.forEach((team) => groups.set(formatTeamName(team.value || team.name), []));

        filteredEmployees.forEach((employee) => {
            const teamName = formatTeamName(employee.team);
            if (!groups.has(teamName)) groups.set(teamName, []);
            groups.get(teamName).push(employee);
        });

        return Array.from(groups.entries())
            .filter(([, members]) => members.length || !search)
            .sort(([a], [b]) => a.localeCompare(b));
    }, [filteredEmployees, search, teams]);

    const exportTeamOptions = useMemo(() => {
        const options = new Map();
        [...employees.map((employee) => employee.team), ...teams.map((team) => team.value || team.name)]
            .filter(Boolean)
            .forEach((team) => {
                const key = normalizeTeamName(team);
                if (key && key !== "others" && !options.has(key)) {
                    options.set(key, formatTeamName(team));
                }
            });

        return Array.from(options.entries())
            .map(([key, label]) => ({ key, label }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [employees, teams]);

    const selectedReports = useMemo(() => {
        if (!selectedEmployee) return [];
        return reports
            .filter(
                (report) =>
                    report.employee?._id === selectedEmployee._id &&
                    inDateRange(report, startDate, endDate),
            )
            .sort((a, b) => new Date(b.date) - new Date(a.date));
    }, [reports, selectedEmployee, startDate, endDate]);

    const selectedTeamReports = useMemo(() => {
        if (!selectedEmployee) return [];
        const selectedTeam = normalizeTeamName(selectedEmployee.team);
        if (!selectedTeam) return [];
        return reports
            .filter(
                (report) =>
                    normalizeTeamName(report.employee?.team) === selectedTeam &&
                    inDateRange(report, startDate, endDate),
            )
            .sort((a, b) => new Date(b.date) - new Date(a.date));
    }, [reports, selectedEmployee, startDate, endDate]);

    const activeReport = useMemo(
        () => selectedReports.find((report) => report._id === activeReportId) || selectedReports[0] || null,
        [activeReportId, selectedReports],
    );

    useEffect(() => {
        document.title = "EOD Reports | Savrion Technologies";
        if (!isModalOpen || !activeReport?.salesVisitReport?.photos?.length) return undefined;

        let cancelled = false;
        const photosWithLocation = activeReport.salesVisitReport.photos
            .map((photo) => photo.location)
            .filter((location) => location?.latitude && location?.longitude);

        photosWithLocation.forEach(async (location) => {
            const key = locationCacheKey(location);
            if (!key || locationNames[key]) return;

            setLocationNames((current) => ({ ...current, [key]: "loading" }));

            try {
                const response = await api.get("/api/admin/reverse-geocode", {
                    params: {
                        lat: location.latitude,
                        lng: location.longitude,
                    },
                });
                const address = response.data?.name || response.data?.displayName || "";
                if (!cancelled) {
                    setLocationNames((current) => ({
                        ...current,
                        [key]: address || "Location name unavailable",
                    }));
                }
            } catch {
                if (!cancelled) {
                    setLocationNames((current) => ({
                        ...current,
                        [key]: "Location name unavailable",
                    }));
                }
            }
        });

        return () => {
            cancelled = true;
        };
    }, [activeReport, isModalOpen, locationNames]);

    const stats = useMemo(() => {
        const submitted = selectedReports.filter((report) => report.status === "submitted").length;
        const drafts = selectedReports.filter((report) => report.status === "draft").length;
        return {
            employeeReports: selectedReports.length,
            submitted,
            drafts,
            teamReports: selectedTeamReports.length,
        };
    }, [selectedReports, selectedTeamReports]);

    const currentMonthLabel = useMemo(() => {
        return new Date().toLocaleDateString("en-IN", {
            month: "long",
            year: "numeric"
        });
    }, []);

    const getMonthYearLabel = (dateStr) => {
        return new Date(dateStr).toLocaleDateString("en-IN", {
            month: "long",
            year: "numeric"
        });
    };

    const groupedTimeline = useMemo(() => {
        if (!selectedEmployee) return [];
        const allDatesSet = new Set([
            ...selectedReports.map(r => r.date.split('T')[0]),
            ...attendance.map(a => a.date)
        ]);
        addOffDaysInRange(allDatesSet, holidays);
        const sortedDates = Array.from(allDatesSet).sort((a, b) => {
            const dateA = parseDateKey(a);
            const dateB = parseDateKey(b);
            return (dateB?.getTime() || 0) - (dateA?.getTime() || 0);
        });

        const groups = {};
        sortedDates.forEach(dateStr => {
            const report = selectedReports.find(r => r.date.startsWith(dateStr));
            const att = attendance.find(a => a.date === dateStr);
            const holiday = holidays.find((item) => dateKeyFromValue(item.date) === dateStr);

            if (statusFilter === "submitted" && (!report || report.status !== "submitted")) return;
            if (statusFilter === "draft" && (!report || report.status !== "draft")) return;

            const monthLabel = getMonthYearLabel(dateStr);
            if (!groups[monthLabel]) groups[monthLabel] = [];
            groups[monthLabel].push({ dateStr, report, att, holiday });
        });

        return Object.entries(groups);
    }, [selectedReports, attendance, holidays, statusFilter, selectedEmployee]);

    const groupedTeamTimeline = useMemo(() => {
        if (!selectedEmployee) return [];
        const groups = {};
        selectedTeamReports.forEach(report => {
            const dateStr = report.date.split('T')[0];
            const monthLabel = getMonthYearLabel(dateStr);
            if (!groups[monthLabel]) groups[monthLabel] = [];
            groups[monthLabel].push({ dateStr, report, att: null });
        });
        return Object.entries(groups);
    }, [selectedTeamReports, selectedEmployee]);

    const timelineGroups = useMemo(() => {
        return statusFilter === "team" ? groupedTeamTimeline : groupedTimeline;
    }, [statusFilter, groupedTimeline, groupedTeamTimeline]);


    const handleExport = (scopeOverride = exportScope) => {
        let exportData = [];
        let exportLabel = "eod_reports";

        if (scopeOverride === "selected") {
            exportData = selectedReports;
            exportLabel = selectedEmployee ? `${selectedEmployee.name}_eod` : "selected_employee_eod";
        } else if (scopeOverride === "selected-team") {
            exportData = selectedTeamReports;
            exportLabel = `${formatTeamName(selectedEmployee?.team)}_team_eod`;
        } else if (scopeOverride === "all") {
            exportData = reports
                .filter((report) => inDateRange(report, startDate, endDate))
                .sort((a, b) => new Date(b.date) - new Date(a.date));
            exportLabel = "all_teams_eod";
        } else {
            const teamKey = scopeOverride.replace("team:", "");
            const teamLabel = exportTeamOptions.find((team) => team.key === teamKey)?.label || "team";
            exportData = reports
                .filter(
                    (report) =>
                        normalizeTeamName(report.employee?.team) === teamKey &&
                        inDateRange(report, startDate, endDate),
                )
                .sort((a, b) => new Date(b.date) - new Date(a.date));
            exportLabel = `${teamLabel}_team_eod`;
        }

        if (!exportData.length) {
            alert("No EOD reports found for this export.");
            return;
        }

        const rows = exportData.map((report) => {
            const photos = report.salesVisitReport?.photos || [];
            const route = report.salesVisitReport?.routeTracking || {};
            const routeDistance = route.totalDistanceMeters || calculateRouteDistance(route.points || []);
            const metrics = report.teamMetrics || {};
            const manufacturing = report.manufacturingReport || {};
            const manufacturingPhotos = manufacturing.photos || [];

            return {
                Employee: report.employee?.name || "",
                Team: report.employee?.team || "",
                Date: report.date,
                Status: report.status,
                DayVibe: vibeConfigs[report.dayVibe]?.label || report.dayVibe || "",
                TasksCompleted: report.tasksCompleted || 0,
                WorkSummary: report.workSummary || "",
                TasksPending: report.tasksPending || "",
                Blockers: getEmployeeBlockerText(report.blockers),
                TomorrowPlan: report.tomorrowPlan || "",
                EarlyEodReason: report.earlyAccessReason || "",
                EarlyEodReasonSubmittedAt: report.earlyAccessReasonSubmittedAt
                    ? new Date(report.earlyAccessReasonSubmittedAt).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })
                    : "",
                TeamMetrics: Object.keys(metrics).length ? JSON.stringify(metrics) : "",
                PlannedProduction: metrics["Planned Production"] || "",
                ActualProduction: metrics["Actual Production"] || "",
                TargetAchieved: metrics["Target Achieved %"] || "",
                RejectedUnits: metrics["Rejected Units"] || "",
                ReworkQuantity: metrics["Rework Quantity"] || "",
                Downtime: metrics.Downtime || "",
                MachineOrLine: metrics["Machine / Line"] || "",
                PendingQuantity: metrics["Pending Quantity"] || "",
                MaterialShortage: metrics["Material Shortage"] || "",
                Safety5SStatus: metrics["Safety / 5S Status"] || "",
                ManufacturingJobs: joinManufacturingRows(manufacturing.productionJobs, (job) =>
                    `${job.orderNo || "-"} / ${job.product || "-"}: planned ${job.plannedQty || 0}, actual ${job.actualQty || 0}, rejected ${job.rejectedQty || 0}, ${job.status || "status not set"}`,
                ),
                DowntimeReasons: joinManufacturingRows(manufacturing.downtimeReasons, (item) =>
                    `${item.reason || "-"} (${item.machine || "machine not set"}): ${item.durationMinutes || 0} min${item.remarks ? ` - ${item.remarks}` : ""}`,
                ),
                QualityDefects: joinManufacturingRows(manufacturing.qualityDefects, (item) =>
                    `${item.defectType || "-"}: ${item.quantity || 0}${item.rootCause ? `, cause: ${item.rootCause}` : ""}${item.correctiveAction ? `, action: ${item.correctiveAction}` : ""}`,
                ),
                ShiftHandoverNotes: manufacturing.shiftHandoverNotes || "",
                ManufacturingProofPhotoCount: manufacturingPhotos.length,
                ManufacturingProofPhotos: joinManufacturingRows(manufacturingPhotos, (photo, index) =>
                    `Photo ${index + 1}: ${photo.category || photo.name || "proof"}${photo.remarks ? ` - ${photo.remarks}` : ""}`,
                ),
                ShopsVisited: report.salesVisitReport?.shopCount || "",
                ShopVisitDetails: report.salesVisitReport?.details || "",
                ShopVisitPhotoCount: photos.length,
                ShopVisitLocations: photos
                    .map((photo, index) => {
                        const location = formatPhotoLocation(photo.location);
                        return location ? `Photo ${index + 1}: ${location}` : "";
                    })
                    .filter(Boolean)
                    .join(" | "),
                ShopToShopDistances: photos
                    .map((photo, index) => {
                        if (!index) return "";
                        return `Shop ${index} to ${index + 1}: ${formatDistance(calculateDistanceMeters(photos[index - 1]?.location, photo.location))}`;
                    })
                    .filter(Boolean)
                    .join(" | "),
                DistanceFromFirstShop: photos
                    .map((photo, index) => {
                        if (!index) return "";
                        return `Shop 1 to ${index + 1}: ${formatDistance(calculateDistanceMeters(photos[0]?.location, photo.location))}`;
                    })
                    .filter(Boolean)
                    .join(" | "),
                LiveTrackingDistance: routeDistance ? formatDistance(routeDistance) : "",
                LiveTrackingPoints: route.points?.length || 0,
            };
        });

        // Sort the rows so that records are grouped correctly and not confusingly mixed
        const sortedRows = [...rows].sort((a, b) => {
            // 1. Sort by Team (case-insensitive)
            const teamA = String(a.Team || "").toLowerCase();
            const teamB = String(b.Team || "").toLowerCase();
            if (teamA !== teamB) return teamA.localeCompare(teamB);

            // 2. Sort by Employee (case-insensitive)
            const nameA = String(a.Employee || "").toLowerCase();
            const nameB = String(b.Employee || "").toLowerCase();
            if (nameA !== nameB) return nameA.localeCompare(nameB);

            // 3. Sort by Date (descending)
            const dateA = new Date(a.Date || 0);
            const dateB = new Date(b.Date || 0);
            return dateB - dateA;
        });

        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.json_to_sheet(sortedRows);
        XLSX.utils.book_append_sheet(workbook, worksheet, "EOD Reports");
        XLSX.writeFile(workbook, `${slugifyFilePart(exportLabel)}_${Date.now()}.${exportFormat}`);
    };

    if (loading) {
        return (
            <div className="flex min-h-screen flex-col bg-[var(--bg-primary)] text-[var(--text-primary)] xl:flex-row">
                <AdminNavbar />
                <main className="min-w-0 flex-1 flex items-center justify-center font-bold text-lg">
                    <div className="flex flex-col items-center gap-3">
                        <div className="w-10 h-10 border-4 border-violet-500 border-t-transparent rounded-full animate-spin"></div>
                        <p>Loading EOD workspace...</p>
                    </div>
                </main>
            </div>
        );
    }

    return (
        <div className="flex min-h-screen flex-col bg-[var(--bg-primary)] text-[var(--text-primary)] xl:flex-row">
            <AdminNavbar />

            <main className="min-w-0 flex-1">
                <div className="mx-auto w-full max-w-[1600px] px-4 py-6 md:px-8">
                    {/* Sleek Modern Header */}
                    <section className="mb-6 rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)] p-6 shadow-sm purple-glow">
                        <div className="flex flex-col gap-5 xl:flex-row xl:items-center xl:justify-between">
                            <div className="flex items-start gap-4">
                                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-[var(--accent-primary)] text-white shadow-md shadow-[var(--accent-primary)]/20">
                                    <FileText size={26} />
                                </div>
                                <div>
                                    <div className="flex items-center gap-2">
                                        <span className="rounded-full bg-[var(--accent-subtle)] px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wider text-[var(--accent-primary)]">
                                            Workspace
                                        </span>
                                    </div>
                                    <h1 className="mt-1 text-2xl font-black tracking-tight md:text-3xl bg-clip-text text-transparent bg-gradient-to-r from-[var(--text-primary)] to-[var(--text-secondary)]">
                                        Team Submission Workspace
                                    </h1>
                                    <p className="mt-1.5 text-xs font-semibold text-[var(--text-secondary)] flex items-center gap-2">
                                        {selectedEmployee ? (
                                            <>
                                                <span className="font-bold text-[var(--text-primary)]">{formatTeamName(selectedEmployee.team)}</span>
                                                <span className="text-gray-300 dark:text-gray-700">|</span>
                                                <span>{selectedEmployee.name}</span>
                                            </>
                                        ) : (
                                            "Select an employee to review reports"
                                        )}
                                    </p>
                                </div>
                            </div>

                            <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
                                <div className="grid grid-cols-2 gap-3 sm:flex">
                                    <DateInput label="From" value={startDate} onChange={setStartDate} />
                                    <DateInput label="To" value={endDate} onChange={setEndDate} />
                                </div>

                                <div className="flex flex-wrap items-center gap-3">
                                    <div className="relative">
                                        <select
                                            value={exportFormat}
                                            onChange={(event) => setExportFormat(event.target.value)}
                                            className="h-11 rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] pl-3 pr-8 text-sm font-bold outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] appearance-none cursor-pointer"
                                        >
                                            <option value="xlsx">XLSX</option>
                                            <option value="xls">XLS</option>
                                            <option value="csv">CSV</option>
                                        </select>
                                        <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-[var(--text-secondary)]" />
                                    </div>

                                    <button
                                        type="button"
                                        onClick={() => handleExport("selected-team")}
                                        disabled={!selectedEmployee}
                                        className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-[var(--accent-primary)] px-5 text-sm font-bold text-white shadow-lg shadow-[var(--accent-primary)]/15 hover:shadow-[var(--accent-primary)]/25 hover:-translate-y-0.5 transition-all duration-200 hover:bg-[var(--accent-hover)] disabled:opacity-50 disabled:pointer-events-none"
                                    >
                                        <Download size={17} />
                                        Export {selectedEmployee ? formatTeamName(selectedEmployee.team) : "Team"}
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => handleExport("all")}
                                        className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] px-5 text-sm font-bold transition-all duration-200 hover:border-[var(--accent-primary)] hover:text-[var(--accent-primary)] hover:-translate-y-0.5"
                                    >
                                        <Download size={17} />
                                        Export All Teams
                                    </button>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Three-Column Modern Layout */}
                    <section className="grid gap-6 xl:grid-cols-[320px_minmax(0,1fr)_320px]">
                        
                        {/* Left Column: Directory Search and Teams Accordion */}
                        <aside className="eod-directory-panel rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)] shadow-sm overflow-hidden flex flex-col h-[fit-content] max-h-[calc(100vh-120px)] sticky top-6">
                            <div className="border-b border-[var(--border-color)] p-4 bg-gradient-to-b from-[var(--bg-secondary)]/50 to-transparent">
                                <h3 className="text-xs font-black uppercase tracking-wider text-[var(--text-secondary)] mb-3 px-1">
                                    Employees & Teams
                                </h3>
                                <div className="relative">
                                    <Search
                                        size={16}
                                        className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]"
                                    />
                                    <input
                                        value={search}
                                        onChange={(event) => setSearch(event.target.value)}
                                        placeholder="Search employee or team..."
                                        className="h-11 w-full rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] pl-10 pr-3 text-sm outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] transition-all duration-200"
                                    />
                                </div>
                            </div>

                            <div className="overflow-y-auto p-3 space-y-2 flex-1 scrollbar-thin max-h-[500px] xl:max-h-[calc(100vh-270px)]">
                                {groupedByTeam.map(([teamName, members]) => (
                                    <div key={teamName} className="mb-2">
                                        <button
                                            type="button"
                                            onClick={() =>
                                                setExpandedTeams((prev) => ({
                                                    ...prev,
                                                    [teamName]: !prev[teamName],
                                                }))
                                            }
                                            className="flex w-full items-center justify-between rounded-xl px-3 py-2.5 text-left text-xs font-black uppercase tracking-wider text-[var(--text-secondary)] hover:bg-[var(--bg-primary)] hover:text-[var(--text-primary)] transition-all duration-200"
                                        >
                                            <span className="flex items-center gap-2">
                                                {expandedTeams[teamName] ? (
                                                    <ChevronUp size={14} className="text-[var(--accent-primary)]" />
                                                ) : (
                                                    <ChevronDown size={14} />
                                                )}
                                                {teamName}
                                            </span>
                                            <span className="rounded-full bg-[var(--border-color)] px-2.5 py-0.5 text-[10px] font-bold text-[var(--text-secondary)]">
                                                {members.length}
                                            </span>
                                        </button>

                                        <AnimatePresence initial={false}>
                                            {expandedTeams[teamName] && (
                                                <motion.div
                                                    initial={{ opacity: 0, height: 0 }}
                                                    animate={{ opacity: 1, height: "auto" }}
                                                    exit={{ opacity: 0, height: 0 }}
                                                    transition={{ duration: 0.2 }}
                                                    className="mt-1 space-y-1 pl-2 overflow-hidden"
                                                >
                                                    {members.length ? (
                                                        members.map((employee) => {
                                                            const isSelected = selectedEmployee?._id === employee._id;
                                                            const nameInitial = employee.name?.charAt(0)?.toUpperCase() || "E";
                                                            return (
                                                                <button
                                                                    key={employee._id}
                                                                    type="button"
                                                                    onClick={() => setSelectedEmployee((prev) => prev?._id === employee._id ? null : employee)}
                                                                    className={`w-full rounded-xl border p-2.5 text-left transition-all duration-200 ${
                                                                        isSelected
                                                                            ? "border-violet-500 bg-[var(--accent-subtle)] text-[var(--accent-primary)] shadow-sm"
                                                                            : "border-transparent bg-transparent hover:bg-[var(--bg-primary)] hover:border-[var(--border-color)] text-[var(--text-primary)]"
                                                                    }`}
                                                                >
                                                                    <div className="flex items-center gap-3">
                                                                        <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-xs font-black overflow-hidden ${
                                                                            isSelected
                                                                                ? "bg-[var(--accent-primary)] text-white shadow-sm"
                                                                                : "bg-[var(--border-color)] text-[var(--text-secondary)]"
                                                                        }`}>
                                                                            {employee.avatar ? (
                                                                                <img src={buildBackendUrl(employee.avatar)} alt="Avatar" className="w-full h-full object-cover" />
                                                                            ) : (
                                                                                nameInitial
                                                                            )}
                                                                        </div>
                                                                        <div className="min-w-0 flex-1">
                                                                            <p className="truncate text-sm font-bold leading-tight">
                                                                                {employee.name}
                                                                            </p>
                                                                            <p className="truncate text-[10px] font-semibold text-[var(--text-secondary)] mt-0.5">
                                                                                {formatRoleLabel(employee.role) || employee.designation || teamName}
                                                                            </p>
                                                                        </div>
                                                                    </div>
                                                                </button>
                                                            );
                                                        })
                                                    ) : (
                                                        <p className="px-3 py-2 text-xs text-[var(--text-secondary)] italic">
                                                            No employees
                                                        </p>
                                                    )}
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                ))}
                            </div>
                        </aside>

                        {/* Middle Column: Stats Metrics and EOD Timeline Feed */}
                        <section className="min-w-0 space-y-6">
                            {selectedEmployee ? (
                                <>
                                    {/* Stats Metric Panel */}
                                    <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-4">
                                        <Metric 
                                            title="Employee Reports" 
                                            value={stats.employeeReports} 
                                            icon={<FileText size={20} />} 
                                            onClick={() => setStatusFilter("all")}
                                            isActive={statusFilter === "all"}
                                        />
                                        <Metric 
                                            title="Team Reports" 
                                            value={stats.teamReports} 
                                            icon={<Activity size={20} />} 
                                            onClick={() => setStatusFilter("team")}
                                            isActive={statusFilter === "team"}
                                        />
                                        <Metric 
                                            title="Submitted" 
                                            value={stats.submitted} 
                                            icon={<CheckCircle2 size={20} />} 
                                            colorClass="from-emerald-500/10 to-teal-500/10 text-emerald-500" 
                                            onClick={() => setStatusFilter("submitted")}
                                            isActive={statusFilter === "submitted"}
                                        />
                                        <Metric 
                                            title="Drafts" 
                                            value={stats.drafts} 
                                            icon={<Clock3 size={20} />} 
                                            colorClass="from-amber-500/10 to-orange-500/10 text-amber-500" 
                                            onClick={() => setStatusFilter("draft")}
                                            isActive={statusFilter === "draft"}
                                        />
                                    </div>

                                    {/* Reports Feed & Timeline */}
                                    <div className="rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)] shadow-sm overflow-hidden">
                                        <div className="flex flex-col gap-4 border-b border-[var(--border-color)] p-5 bg-gradient-to-b from-[var(--bg-secondary)]/50 to-transparent md:flex-row md:items-center md:justify-between">
                                            <div>
                                                <p className="text-[10px] font-black uppercase tracking-wider text-[var(--accent-primary)]">
                                                    Report Timeline
                                                </p>
                                                <h2 className="text-xl font-black mt-0.5">{selectedEmployee.name}</h2>
                                            </div>

                                            <div className="flex flex-wrap items-center gap-2.5">
                                                <button
                                                    type="button"
                                                    onClick={() => handleExport("selected")}
                                                    className="inline-flex h-10 items-center gap-2 rounded-xl bg-[var(--accent-primary)] px-4 text-xs font-bold text-white transition-all duration-200 hover:bg-[var(--accent-hover)] hover:-translate-y-0.5 shadow-sm"
                                                >
                                                    <Download size={14} />
                                                    Export {selectedEmployee ? selectedEmployee.name : "Employee"}
                                                </button>
                                            </div>
                                        </div>

                                        <div className="eod-timeline-container p-5 relative">
                                            {/* Vertical line indicator behind the timeline items */}
                                            {timelineGroups.length > 0 && (
                                                <div className="eod-timeline-line absolute left-[37px] top-8 bottom-8 w-[2px] bg-gradient-to-b from-[var(--accent-primary)]/20 via-[var(--border-color)] to-transparent" />
                                            )}

                                            <div className="space-y-6">
                                                {timelineGroups.length ? (
                                                     timelineGroups.map(([monthLabel, items]) => {
                                                         const isCollapsed = collapsedMonths[monthLabel] !== undefined
                                                             ? collapsedMonths[monthLabel]
                                                             : monthLabel !== currentMonthLabel;
                                                         return (
                                                             <div key={monthLabel} className="space-y-4">
                                                                 {/* Month Section Header */}
                                                                 <button
                                                                     type="button"
                                                                     onClick={() => toggleMonth(monthLabel)}
                                                                     className="eod-month-header relative z-10 flex w-full items-center gap-4 py-2 text-left group"
                                                                 >
                                                                     <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[var(--border-color)] bg-[var(--card-bg)] shadow-sm group-hover:border-[var(--accent-primary)] transition-colors duration-200">
                                                                         <CalendarDays size={16} className="text-[var(--accent-primary)]" />
                                                                     </div>
                                                                     <div className="eod-month-line h-[1px] flex-1 bg-[var(--border-color)] mr-2 flex items-center justify-between">
                                                                         <span className="text-xs font-black uppercase tracking-wider text-[var(--text-secondary)] bg-[var(--card-bg)] px-3 -ml-1 group-hover:text-[var(--accent-primary)] transition-colors duration-200 flex items-center gap-2">
                                                                             {monthLabel}
                                                                             <span className="text-[10px] font-bold text-gray-400 dark:text-gray-600">
                                                                                 ({items.length} days)
                                                                             </span>
                                                                         </span>
                                                                         <span className="bg-[var(--card-bg)] pl-2 text-[var(--text-secondary)] group-hover:text-[var(--accent-primary)] transition-colors duration-200">
                                                                             {isCollapsed ? (
                                                                                 <ChevronDown size={14} />
                                                                             ) : (
                                                                                 <ChevronUp size={14} />
                                                                             )}
                                                                         </span>
                                                                     </div>
                                                                 </button>

                                                                 {/* Timeline Items under this month */}
                                                                 <AnimatePresence initial={false}>
                                                                     {!isCollapsed && (
                                                                         <motion.div
                                                                             initial={{ opacity: 0, height: 0 }}
                                                                             animate={{ opacity: 1, height: "auto" }}
                                                                             exit={{ opacity: 0, height: 0 }}
                                                                             transition={{ duration: 0.2 }}
                                                                             className="space-y-4 overflow-hidden"
                                                                         >
                                                                             {items.map(({ dateStr, report, att, holiday }) => {
                                                                                 const isSunday = isSundayDate(dateStr);
                                                                                 const isHoliday = Boolean(holiday);
                                                                                 if (report) {
                                                                                     const isActive = activeReport?._id === report._id;
                                                                                     const vibe = vibeConfigs[report.dayVibe] || { label: report.dayVibe || "No vibe", emoji: "ðŸ“‹", color: "text-gray-500 bg-[var(--bg-secondary)]0/10 border-gray-500/20" };
                                                                                     const statusStyle = statusStyles[report.status] || statusStyles.draft;
                                                                                    const hasBlocker = hasEmployeeBlockerText(report.blockers);

                                                                                     return (
                                                                                         <div key={report._id} className="eod-timeline-item relative flex gap-4 group">
                                                                                             {/* Timeline Dot */}
                                                                                             <div className="relative z-10 flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[var(--border-color)] bg-[var(--card-bg)] shadow-sm transition-all duration-300 group-hover:scale-105">
                                                                                                 <div className={`h-6 w-6 rounded-full flex items-center justify-center text-sm ${isActive ? "bg-[var(--accent-subtle)] text-violet-500" : "text-gray-400"}`}>
                                                                                                     <CalendarDays size={14} />
                                                                                                 </div>
                                                                                             </div>

                                                                                             {/* Report Card */}
                                                                                             <button
                                                                                                 type="button"
                                                                                                 onClick={() => {
                                                                                                     setActiveReportId(report._id);
                                                                                                     setIsModalOpen(true);
                                                                                                 }}
                                                                                                 className={`eod-timeline-card flex-1 text-left rounded-2xl border p-4 transition-all duration-300 ${
                                                                                                     isActive
                                                                                                         ? "border-[var(--accent-primary)]/40 bg-[var(--accent-subtle)]/50 shadow-sm shadow-[var(--accent-primary)]/5"
                                                                                                         : "border-[var(--border-color)] bg-[var(--bg-secondary)] hover:border-violet-500/30 hover:shadow-sm"
                                                                                                 }`}
                                                                                             >
                                                                                                 <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                                                                                                     <div className="space-y-1">
                                                                                                         <div className="flex flex-wrap items-center gap-2">
                                                                                                             <h3 className="text-sm font-black tracking-tight text-[var(--text-primary)]">
                                                                                                                 {statusFilter === "team" && report.employee?.name 
                                                                                                                     ? `${report.employee.name} • ${formatDate(report.date)}` 
                                                                                                                     : formatDate(report.date)
                                                                                                                 }
                                                                                                             </h3>
                                                                                                             {(isSunday || isHoliday) && (
                                                                                                                 <span className="rounded-full border border-sky-500/20 bg-sky-500/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-sky-600 dark:text-sky-300">
                                                                                                                     {isHoliday ? holiday.name || "Public holiday" : "Worked on Sunday"}
                                                                                                                 </span>
                                                                                                             )}
                                                                                                             <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold tracking-wide uppercase ${statusStyle}`}>
                                                                                                                 {report.status || "draft"}
                                                                                                             </span>
                                                                                                             {hasBlocker && (
                                                                                                                 <span className="rounded-full border border-red-500/20 bg-red-500/10 px-2 py-0.5 text-[10px] font-bold text-red-500 animate-pulse">
                                                                                                                     Blocked
                                                                                                                 </span>
                                                                                                             )}
                                                                                                             {report.earlyAccessReason && (
                                                                                                                 <span className="inline-flex items-center gap-1 rounded-full border border-amber-500/20 bg-amber-500/10 px-2 py-0.5 text-[10px] font-bold text-amber-600 dark:text-amber-300">
                                                                                                                     <Clock3 size={11} />
                                                                                                                     Early EOD
                                                                                                                 </span>
                                                                                                             )}
                                                                                                             {isSalesReport(report) && hasSalesProof(report) && (
                                                                                                                 <span className="inline-flex items-center gap-1 rounded-full border border-[var(--accent-primary)]/20 bg-[var(--accent-subtle)] px-2 py-0.5 text-[10px] font-bold text-[var(--accent-primary)] dark:text-[var(--heading-secondary)]">
                                                                                                                     <Camera size={11} />
                                                                                                                     Sales proof
                                                                                                                 </span>
                                                                                                             )}
                                                                                                         </div>
                                                                                                         {!isSalesReport(report) ? (
                                                                                                             <p className="text-xs leading-relaxed text-[var(--text-secondary)] line-clamp-2 mt-1">
                                                                                                                 {report.workSummary || "No work summary added."}
                                                                                                             </p>
                                                                                                         ) : (
                                                                                                             <p className="mt-1 text-xs font-bold text-[var(--accent-primary)] flex items-center gap-1.5">
                                                                                                                 <Store size={12} />
                                                                                                                 {(report.salesVisitReport?.photos || []).length} shop visit{(report.salesVisitReport?.photos || []).length === 1 ? "" : "s"} logged
                                                                                                             </p>
                                                                                                         )}
                                                                                                         {isSalesReport(report) && hasSalesProof(report) && (
                                                                                                             <p className="mt-2 inline-flex items-center gap-1 text-[10px] font-bold text-[var(--accent-primary)]">
                                                                                                                 <MapPin size={11} />
                                                                                                                 {report.salesVisitReport.photos.length} photo location{report.salesVisitReport.photos.length === 1 ? "" : "s"} attached
                                                                                                             </p>
                                                                                                         )}
                                                                                                     </div>

                                                                                                     <div className="flex flex-wrap items-center gap-2 self-start shrink-0 text-xs mt-1 sm:mt-0">
                                                                                                         <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-bold ${vibe.color}`}>
                                                                                                             <span>{vibe.emoji}</span>
                                                                                                             <span>{vibe.label}</span>
                                                                                                         </span>
                                                                                                         {!isSalesReport(report) ? (
                                                                                                             <span className="rounded-lg bg-[var(--bg-primary)] border border-[var(--border-color)] px-2 py-1 font-black text-[var(--text-primary)]">
                                                                                                                 {report.tasksCompleted || 0} tasks
                                                                                                             </span>
                                                                                                         ) : (
                                                                                                             <span className="rounded-lg bg-[var(--accent-subtle)] border border-[var(--accent-primary)]/20 px-2 py-1 font-black text-[var(--accent-primary)] dark:text-[var(--heading-secondary)]">
                                                                                                                 {(report.salesVisitReport?.photos || []).length} shops
                                                                                                             </span>
                                                                                                         )}
                                                                                                     </div>
                                                                                                 </div>
                                                                                             </button>
                                                                                         </div>
                                                                                     );
                                                                                 } else {
                                                                                     // Weekly off, absent, or missing EOD view card
                                                                                     const isWeeklyOff = (isSunday || isHoliday) && !att;
                                                                                     const isAbsent = !isWeeklyOff && (!att || att.status === 'absent' || !att.checkIn);
                                                                                     
                                                                                     return (
                                                                                         <div key={dateStr} className="eod-timeline-item relative flex gap-4 group">
                                                                                             {/* Timeline Dot */}
                                                                                             <div className="relative z-10 flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[var(--border-color)] bg-[var(--card-bg)] shadow-sm">
                                                                                                 <div className="h-6 w-6 rounded-full flex items-center justify-center text-sm text-gray-400">
                                                                                                     <CalendarDays size={14} />
                                                                                                 </div>
                                                                                             </div>

                                                                                             {/* Status Card (Static) */}
                                                                                             <div
                                                                                                 className={`eod-timeline-card flex-1 rounded-2xl border p-4 ${
                                                                                                     isWeeklyOff
                                                                                                         ? "border-sky-500/20 bg-sky-500/5 dark:bg-sky-950/10"
                                                                                                         : isAbsent
                                                                                                         ? "border-rose-500/20 bg-rose-500/5 dark:bg-rose-950/10"
                                                                                                         : "border-amber-500/20 bg-amber-500/5 dark:bg-amber-950/10"
                                                                                                 }`}
                                                                                             >
                                                                                                 <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                                                                                                     <div className="space-y-1">
                                                                                                         <div className="flex flex-wrap items-center gap-2">
                                                                                                             <h3 className="text-sm font-black tracking-tight text-[var(--text-primary)]">
                                                                                                                 {formatDate(dateStr)}
                                                                                                             </h3>
                                                                                                             {(isSunday || isHoliday) && (
                                                                                                                 <span className="rounded-full border border-sky-500/20 bg-sky-500/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-sky-600 dark:text-sky-300">
                                                                                                                     {isHoliday ? holiday.name || "Public holiday" : "Sunday"}
                                                                                                                 </span>
                                                                                                             )}
                                                                                                             <span
                                                                                                                 className={`rounded-full border px-2.5 py-0.5 text-[10px] font-bold tracking-wide ${
                                                                                                                     isWeeklyOff
                                                                                                                         ? "bg-sky-500/10 text-sky-600 border-sky-500/20 dark:text-sky-300"
                                                                                                                         : isAbsent
                                                                                                                         ? "bg-rose-500/10 text-rose-500 border-rose-500/20"
                                                                                                                         : "bg-amber-500/10 text-amber-600 border-amber-500/20 dark:text-amber-400"
                                                                                                                 }`}
                                                                                                             >
                                                                                                                 {isWeeklyOff ? (isHoliday ? "public holiday" : "weekly off") : isAbsent ? "absent in that date" : "didn;t submitted the report"}
                                                                                                             </span>
                                                                                                         </div>
                                                                                                         <p className="text-xs leading-relaxed text-[var(--text-secondary)] mt-1">
                                                                                                             {isWeeklyOff
                                                                                                                 ? `${isHoliday ? holiday.name || "Public holiday" : "Sunday weekly off"}. No EOD report expected.`
                                                                                                                 : isAbsent 
                                                                                                                 ? "Employee was marked absent on this date." 
                                                                                                                 : "Employee checked in but didn't submit the EOD report."
                                                                                                             }
                                                                                                         </p>
                                                                                                     </div>
                                                                                                 </div>
                                                                                             </div>
                                                                                         </div>
                                                                                     );
                                                                                 }
                                                                             })}
                                                                         </motion.div>
                                                                     )}
                                                                 </AnimatePresence>
                                                             </div>
                                                         );
                                                     })
                                                ) : (
                                                    <div className="p-12 text-center text-sm text-[var(--text-secondary)] bg-[var(--bg-primary)] rounded-2xl border border-dashed border-[var(--border-color)]">
                                                        {statusFilter === "team" 
                                                            ? "No EOD reports found for this team." 
                                                            : `No ${statusFilter !== "all" ? statusFilter : ""} EOD reports found for this employee.`
                                                        }
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <div className="rounded-2xl border border-dashed border-[var(--border-color)] bg-[var(--card-bg)] p-12 text-center shadow-sm">
                                    <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-[var(--accent-primary)]/10 text-[var(--accent-primary)]">
                                        <FileText size={24} />
                                    </div>
                                    <h2 className="text-xl font-black text-[var(--text-primary)]">Select an employee</h2>
                                    <p className="mx-auto mt-2 max-w-md text-sm font-semibold text-[var(--text-secondary)]">
                                        Open a team from the left panel and choose an employee to view their EOD timeline.
                                    </p>
                                </div>
                            )}
                        </section>

                        {/* Right Column: Profile widget */}
                        {selectedEmployee && (
                        <aside className="space-y-6">
                            
                            {/* Profile Information Widget */}
                            <section className="rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)] shadow-sm overflow-hidden relative">
                                {/* Decorative Header Gradient */}
                                <div className="h-24 bg-[var(--accent-primary)] relative">
                                    <div className="absolute inset-0 bg-black/10" />
                                    <div className="absolute top-4 right-4">
                                        <span className="rounded-full bg-[var(--card-bg)]/20 backdrop-blur-md px-2.5 py-0.5 text-[9px] font-black uppercase tracking-wider text-white">
                                            Admin Panel
                                        </span>
                                    </div>
                                </div>

                                {/* Avatar & Primary Info */}
                                <div className="px-5 pb-5 pt-0 relative flex flex-col items-center -mt-10">
                                    <div className="flex h-20 w-20 items-center justify-center rounded-full border-4 border-[var(--card-bg)] bg-[var(--accent-primary)] text-white font-black text-2xl shadow-md">
                                        {selectedEmployee?.name?.charAt(0)?.toUpperCase()}
                                    </div>
                                    
                                    <h2 className="font-black text-base text-[var(--text-primary)] mt-3 text-center leading-tight">
                                        {selectedEmployee?.name || "No employee selected"}
                                    </h2>
                                    <span className="mt-1 text-xs font-semibold text-[var(--text-secondary)]">
                                        {formatRoleLabel(selectedEmployee?.role) || selectedEmployee?.designation || "Team Member"}
                                    </span>

                                    {/* Info Rows */}
                                    <div className="w-full mt-6 space-y-2.5 text-xs border-t border-b border-[var(--border-color)] py-4">
                                        <InfoRow label="Department" value={formatTeamName(selectedEmployee?.team)} />
                                        <InfoRow label="Email Address" value={selectedEmployee?.email || "-"} />
                                        <InfoRow label="Employment Role" value={formatRoleLabel(selectedEmployee?.role) || selectedEmployee?.designation || "-"} />
                                    </div>
                                </div>
                            </section>
                        </aside>
                        )}

                    </section>
                </div>
            </main>

            {/* EOD Report Detail Modal Popup */}
            <AnimatePresence>
                {isModalOpen && activeReport && (
                    <div className="fixed inset-0 z-[150] flex items-center justify-center p-4">
                        {/* Backdrop with Blur */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setIsModalOpen(false)}
                            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                        />

                        {/* Modal Box Container */}
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: 20 }}
                            transition={{ type: "spring", duration: 0.4 }}
                            className="relative w-full max-w-2xl rounded-3xl bg-[var(--card-bg)] border border-[var(--border-color)] overflow-hidden shadow-2xl flex flex-col max-h-[85vh] z-10"
                        >
                            {/* Header Banner styling */}
                            <div className="h-28 bg-[var(--accent-primary)] relative shrink-0">
                                <div className="absolute inset-0 bg-black/10" />
                                <button
                                    type="button"
                                    onClick={() => setIsModalOpen(false)}
                                    className="absolute top-4 right-4 flex h-9 w-9 items-center justify-center rounded-full bg-[var(--card-bg)]/10 text-white backdrop-blur-md transition hover:bg-[var(--card-bg)]/20"
                                >
                                    <span className="text-xl font-bold leading-none">&times;</span>
                                </button>
                                <div className="absolute bottom-4 left-6 flex items-center gap-3">
                                    <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-white/20 bg-[var(--card-bg)]/10 text-white font-black text-lg backdrop-blur-md">
                                        {selectedEmployee?.name?.charAt(0)?.toUpperCase()}
                                    </div>
                                    <div>
                                        <h3 className="font-black text-white text-base leading-none">
                                            {selectedEmployee?.name}
                                        </h3>
                                        <p className="text-xs text-white/80 font-semibold mt-1.5">
                                            {formatTeamName(selectedEmployee?.team)} &bull; {formatDate(activeReport.date)}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Scrollable EOD Details List */}
                            <div className="p-6 overflow-y-auto space-y-4 flex-1 scrollbar-thin">
                                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[var(--border-color)] pb-3">
                                    <div className="flex items-center gap-2">
                                        <span className={`rounded-full border px-2.5 py-0.5 text-xs font-bold tracking-wide uppercase ${statusStyles[activeReport.status] || statusStyles.draft}`}>
                                            {activeReport.status || "draft"}
                                        </span>
                                        {hasEmployeeBlockerText(activeReport.blockers) && (
                                            <span className="rounded-full border border-red-500/20 bg-red-500/10 px-2.5 py-0.5 text-xs font-bold text-red-500 animate-pulse">
                                                Blocked
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2 text-xs">
                                        <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 font-bold ${vibeConfigs[activeReport.dayVibe]?.color || "text-gray-500 bg-[var(--bg-secondary)]0/10 border-gray-500/20"}`}>
                                            <span>{vibeConfigs[activeReport.dayVibe]?.emoji || "ðŸ“‹"}</span>
                                            <span>{vibeConfigs[activeReport.dayVibe]?.label || activeReport.dayVibe}</span>
                                        </span>
                                        {!isSalesReport(activeReport) ? (
                                            <span className="rounded-lg bg-[var(--bg-primary)] border border-[var(--border-color)] px-2.5 py-1 font-black text-[var(--text-primary)]">
                                                {activeReport.tasksCompleted || 0} tasks completed
                                            </span>
                                        ) : (
                                            <span className="rounded-lg bg-[var(--accent-subtle)] border border-[var(--accent-primary)]/20 px-2.5 py-1 font-black text-[var(--accent-primary)] dark:text-[var(--heading-secondary)]">
                                                {(activeReport.salesVisitReport?.photos || []).length} shops visited
                                            </span>
                                        )}
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    {activeReport.earlyAccessReason && (
                                        <div className="rounded-xl border border-amber-500/25 bg-amber-500/10 p-4 shadow-sm">
                                            <div className="flex flex-wrap items-start justify-between gap-3">
                                                <div className="flex items-center gap-2 text-amber-700 dark:text-amber-300">
                                                    <Clock3 size={16} />
                                                    <p className="text-xs font-black uppercase tracking-wider">Early EOD request</p>
                                                </div>
                                                {activeReport.earlyAccessReasonSubmittedAt && (
                                                    <span className="text-[10px] font-bold text-[var(--text-secondary)]">
                                                        Submitted {new Date(activeReport.earlyAccessReasonSubmittedAt).toLocaleString("en-IN", {
                                                            timeZone: "Asia/Kolkata",
                                                            day: "2-digit",
                                                            month: "short",
                                                            year: "numeric",
                                                            hour: "2-digit",
                                                            minute: "2-digit",
                                                        })}
                                                    </span>
                                                )}
                                            </div>
                                            <p className="mt-3 whitespace-pre-wrap text-sm font-semibold leading-6 text-[var(--text-primary)]">
                                                {activeReport.earlyAccessReason}
                                            </p>
                                        </div>
                                    )}

                                    {activeReport.earlyCheckoutReason && (
                                        <div className="rounded-xl border border-red-500/25 bg-red-500/10 p-4 shadow-sm">
                                            <div className="flex flex-wrap items-start justify-between gap-3">
                                                <div className="flex items-center gap-2 text-red-700 dark:text-red-300">
                                                    <AlertTriangle size={16} />
                                                    <p className="text-xs font-black uppercase tracking-wider">Early Checkout Reason</p>
                                                </div>
                                                {activeReport.earlyCheckoutReasonSubmittedAt && (
                                                    <span className="text-[10px] font-bold text-[var(--text-secondary)]">
                                                        Submitted {new Date(activeReport.earlyCheckoutReasonSubmittedAt).toLocaleString("en-IN", {
                                                            timeZone: "Asia/Kolkata",
                                                            day: "2-digit",
                                                            month: "short",
                                                            year: "numeric",
                                                            hour: "2-digit",
                                                            minute: "2-digit",
                                                        })}
                                                    </span>
                                                )}
                                            </div>
                                            <p className="mt-3 whitespace-pre-wrap text-sm font-semibold leading-6 text-[var(--text-primary)]">
                                                {activeReport.earlyCheckoutReason}
                                            </p>
                                        </div>
                                    )}
                                    {!isSalesReport(activeReport) && (
                                        <>
                                            <DetailBlock 
                                                title="Work Summary" 
                                                value={activeReport.workSummary || "No summary added."} 
                                                icon={<CheckCircle2 size={14} />} 
                                                accentColor="border-emerald-500 text-emerald-600 dark:text-emerald-400" 
                                            />
                                            {activeReport.tasksPending && activeReport.tasksPending.trim() !== "" && (
                                                <DetailBlock 
                                                    title="Pending Tasks" 
                                                    value={activeReport.tasksPending} 
                                                    icon={<Clock3 size={14} />} 
                                                    accentColor="border-amber-500 text-amber-600 dark:text-amber-400" 
                                                />
                                            )}
                                            <DetailBlock 
                                                title="Tomorrow Plan" 
                                                value={activeReport.tomorrowPlan || "No plan added."} 
                                                icon={<Flame size={14} />} 
                                                accentColor="border-violet-500 text-[var(--accent-primary)] dark:text-[var(--accent-primary)]" 
                                            />

                                            {hasEmployeeBlockerText(activeReport.blockers) && (
                                                <DetailBlock
                                                    title="Blockers / Issues"
                                                    value={getEmployeeBlockerText(activeReport.blockers)}
                                                    icon={<AlertTriangle size={14} />}
                                                    accentColor="border-rose-500 text-rose-600 dark:text-rose-400"
                                                />
                                            )}
                                        </>
                                    )}

                                    {activeReport.teamMetrics && Object.keys(activeReport.teamMetrics).length > 0 && (
                                        <div className="rounded-xl bg-[var(--bg-primary)] border border-[var(--border-color)] p-4 shadow-sm">
                                            <p className="mb-3 text-xs font-black uppercase tracking-wider text-[var(--text-primary)]">Team Metrics</p>
                                            <div className="grid grid-cols-2 gap-3">
                                                {Object.entries(activeReport.teamMetrics).map(([key, value]) => (
                                                    <div key={key} className="bg-[var(--card-bg)] border border-[var(--border-color)] rounded-lg p-2.5 shadow-sm text-center">
                                                        <p className="text-[10px] font-bold text-[var(--text-secondary)] uppercase truncate" title={key}>{key}</p>
                                                        <p className="font-black text-base mt-1 text-[var(--text-primary)]">{value || "0"}</p>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {activeReport.manufacturingReport && (
                                        <ManufacturingReportPanel report={activeReport.manufacturingReport} />
                                    )}

                                    {isSalesReport(activeReport) && activeReport.salesVisitReport && (
                                        <div className="rounded-xl bg-[var(--bg-primary)] border border-[var(--border-color)] p-4 shadow-sm">
                                            <div className="mb-4 flex items-center justify-between gap-3">
                                                <p className="text-xs font-black uppercase tracking-wider text-[var(--text-primary)]">
                                                    Shop Visits
                                                </p>
                                                <span className="inline-flex items-center gap-1 rounded-full border border-[var(--accent-primary)]/20 bg-[var(--accent-primary)]/10 px-2.5 py-1 text-[10px] font-black text-[var(--accent-primary)]">
                                                    {(activeReport.salesVisitReport.photos || []).length} shop{(activeReport.salesVisitReport.photos || []).length === 1 ? "" : "s"} visited
                                                </span>
                                            </div>

                                            {(activeReport.salesVisitReport.photos || []).length === 0 && (
                                                <p className="text-xs font-semibold text-[var(--text-secondary)] text-center py-4">No shop visits logged for this day.</p>
                                            )}

                                            <div className="space-y-4">
                                                {(activeReport.salesVisitReport.photos || []).map((photo, index) => (
                                                    <div key={`${photo.name}-${index}`} className="rounded-xl border border-[var(--border-color)] bg-[var(--card-bg)] overflow-hidden">
                                                        {/* Card header */}
                                                        <div className="flex items-center justify-between px-3 py-2 border-b border-[var(--border-color)] bg-[var(--bg-secondary)]">
                                                            <span className="text-[10px] font-black uppercase tracking-wider text-[var(--accent-primary)]">Shop {index + 1}</span>
                                                            {(photo.uploadedAt || photo.capturedAt) && (
                                                                <span className="text-[10px] text-[var(--text-secondary)] font-semibold">
                                                                    {new Date(photo.uploadedAt || photo.capturedAt).toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit" })}
                                                                </span>
                                                            )}
                                                        </div>
                                                        {/* Content */}
                                                        <div className="flex gap-3 p-3">
                                                            <a href={photo.data} target="_blank" rel="noreferrer" className="flex-shrink-0">
                                                                <img
                                                                    src={photo.data}
                                                                    alt={`Shop ${index + 1}`}
                                                                    className="h-20 w-20 rounded-lg object-cover border border-[var(--border-color)] shadow-sm"
                                                                />
                                                            </a>
                                                            <div className="flex-1 flex flex-col gap-1.5 min-w-0">
                                                                {/* Location name */}
                                                                <div className="flex items-start gap-1.5">
                                                                    <MapPin size={11} className="flex-shrink-0 mt-0.5 text-[var(--accent-primary)]" />
                                                                    <span className="text-xs font-bold text-[var(--text-primary)] leading-snug">
                                                                        {photo.locationName || (photo.location?.latitude ? `${Number(photo.location.latitude).toFixed(4)}, ${Number(photo.location.longitude).toFixed(4)}` : "No location")}
                                                                    </span>
                                                                </div>
                                                                {/* GPS coords + map link */}
                                                                {photo.location?.latitude && (
                                                                    <a
                                                                        href={mapUrlForLocation(photo.location)}
                                                                        target="_blank"
                                                                        rel="noreferrer"
                                                                        className="text-[10px] font-bold text-[var(--accent-primary)] underline w-fit"
                                                                    >
                                                                        Open on map ↗
                                                                    </a>
                                                                )}
                                                                {/* Description */}
                                                                {photo.description && (
                                                                    <p className="text-xs text-[var(--text-secondary)] font-medium leading-relaxed whitespace-pre-wrap mt-1">
                                                                        {photo.description}
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Footer Exporter Button and Dismiss */}
                            <div className="border-t border-[var(--border-color)] px-6 py-4 bg-[var(--bg-primary)] flex items-center justify-between shrink-0">
                                <button
                                    type="button"
                                    onClick={() => {
                                        handleExport("selected");
                                        setIsModalOpen(false);
                                    }}
                                    className="inline-flex h-10 items-center gap-2 rounded-xl border border-[var(--border-color)] bg-[var(--card-bg)] px-4 text-xs font-bold transition hover:border-[var(--accent-primary)] hover:text-[var(--accent-primary)] shadow-sm"
                                >
                                    <Download size={14} />
                                    Export This EOD
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setIsModalOpen(false)}
                                    className="inline-flex h-10 items-center justify-center rounded-xl bg-gray-200 dark:bg-gray-800 px-5 text-xs font-bold transition hover:bg-gray-300 dark:hover:bg-gray-700"
                                >
                                    Close
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
};

const DateInput = ({ label, value, onChange }) => (
    <label className="min-w-[140px] block">
        <span className="mb-1 block text-[10px] font-black uppercase tracking-wider text-[var(--text-secondary)]">
            {label}
        </span>
        <input
            type="date"
            value={value}
            onChange={(event) => onChange(event.target.value)}
            className="h-11 w-full rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 text-sm outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] transition-all duration-200"
        />
    </label>
);

const Metric = ({ title, value, icon, onClick, isActive, colorClass = "bg-[var(--accent-subtle)] text-[var(--accent-primary)]" }) => (
    <button
        type="button"
        onClick={onClick}
        className={`w-full text-left rounded-2xl border p-5 shadow-sm transition-all duration-300 relative overflow-hidden group ${
            isActive
                ? "border-violet-500 bg-[var(--accent-subtle)]/60 shadow-md -translate-y-1"
                : "border-[var(--border-color)] bg-[var(--card-bg)] hover:border-violet-500/30 hover:-translate-y-0.5"
        }`}
    >
        <div className="flex items-center justify-between w-full">
            <div>
                <p className="text-[10px] font-black uppercase tracking-wider text-[var(--text-secondary)]">{title}</p>
                <p className="mt-2 text-2xl font-black tracking-tight">{value}</p>
            </div>
            <div className={`flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br ${colorClass} transition-transform duration-300 group-hover:scale-110`}>
                {icon}
            </div>
        </div>
        <div className={`absolute bottom-0 left-0 right-0 h-[3px] bg-gradient-to-r from-transparent via-[var(--accent-primary)] to-transparent transition-transform duration-500 ${
            isActive ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"
        }`} />
    </button>
);

const InfoRow = ({ label, value }) => (
    <div className="flex items-center justify-between gap-3 py-1">
        <span className="text-[var(--text-secondary)] font-semibold">{label}</span>
        <span className="max-w-[170px] truncate font-bold text-[var(--text-primary)] text-right">{value}</span>
    </div>
);

const ManufacturingReportPanel = ({ report = {} }) => {
    const jobs = report.productionJobs || [];
    const downtime = report.downtimeReasons || [];
    const defects = report.qualityDefects || [];
    const photos = report.photos || [];
    const hasContent = jobs.length || downtime.length || defects.length || report.shiftHandoverNotes || photos.length;

    if (!hasContent) return null;

    return (
        <div className="rounded-xl bg-[var(--bg-primary)] border border-[var(--border-color)] p-4 shadow-sm">
            <p className="mb-3 text-xs font-black uppercase tracking-wider text-[var(--text-primary)]">Manufacturing Report</p>

            <ManufacturingTable
                title="Job / order production"
                rows={jobs}
                columns={[
                    ["Order", "orderNo"],
                    ["Product", "product"],
                    ["Plan", "plannedQty"],
                    ["Actual", "actualQty"],
                    ["Reject", "rejectedQty"],
                    ["Status", "status"],
                ]}
            />

            <ManufacturingTable
                title="Downtime reasons"
                rows={downtime}
                columns={[
                    ["Reason", "reason"],
                    ["Machine", "machine"],
                    ["Minutes", "durationMinutes"],
                    ["Remarks", "remarks"],
                ]}
            />

            <ManufacturingTable
                title="Quality defects"
                rows={defects}
                columns={[
                    ["Defect", "defectType"],
                    ["Qty", "quantity"],
                    ["Root cause", "rootCause"],
                    ["Action", "correctiveAction"],
                ]}
            />

            {report.shiftHandoverNotes && (
                <div className="mt-3 rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)] p-3">
                    <p className="text-[10px] font-black uppercase tracking-wide text-[var(--text-secondary)]">Shift handover</p>
                    <p className="mt-1 whitespace-pre-wrap text-xs font-semibold leading-relaxed text-[var(--text-primary)]">{report.shiftHandoverNotes}</p>
                </div>
            )}

            {photos.length > 0 && (
                <div className="mt-3 grid grid-cols-2 gap-3">
                    {photos.map((photo, index) => (
                        <div key={`${photo.name}-${index}`} className="overflow-hidden rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)]">
                            <a href={photo.data} target="_blank" rel="noreferrer">
                                <img src={photo.data} alt={photo.name || `Manufacturing proof ${index + 1}`} className="h-28 w-full object-cover" />
                            </a>
                            <div className="space-y-1 px-2 py-2">
                                <p className="truncate text-[10px] font-black text-[var(--text-primary)]">{photo.category || photo.name || `Proof ${index + 1}`}</p>
                                {photo.remarks && <p className="text-[10px] font-semibold leading-snug text-[var(--text-secondary)]">{photo.remarks}</p>}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

const ManufacturingTable = ({ title, rows = [], columns = [] }) => {
    if (!rows.length) return null;

    return (
        <div className="mb-3 overflow-hidden rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)]">
            <p className="border-b border-[var(--border-color)] px-3 py-2 text-[10px] font-black uppercase tracking-wide text-[var(--text-secondary)]">{title}</p>
            <div className="overflow-x-auto">
                <table className="w-full min-w-[520px] text-left text-[11px]">
                    <thead className="bg-[var(--bg-secondary)] text-[var(--text-secondary)]">
                        <tr>
                            {columns.map(([label]) => (
                                <th key={label} className="px-3 py-2 font-black uppercase">{label}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, index) => (
                            <tr key={index} className="border-t border-[var(--border-color)]">
                                {columns.map(([label, key]) => (
                                    <td key={label} className="px-3 py-2 font-bold text-[var(--text-primary)]">{row[key] || "-"}</td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const DetailBlock = ({ title, value, icon, accentColor = "border-violet-500 text-violet-500" }) => (
    <div className={`rounded-xl bg-[var(--bg-primary)] border-l-4 ${accentColor} p-4 shadow-sm transition-all duration-200 hover:shadow-md`}>
        <div className="mb-2 flex items-center gap-2 text-xs font-black uppercase tracking-wider">
            {icon}
            {title}
        </div>
        <p className="whitespace-pre-wrap text-xs leading-relaxed text-[var(--text-secondary)] font-semibold">{value}</p>
    </div>
);

export default EODReports;


